import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { map } from 'rxjs';

import type { FiltersInterface } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { FilterHeadingComponent } from '@valk-nx/compositions/ui-filters/src/lib/filter-heading/filter-heading';
import { FiltersComponent } from '@valk-nx/compositions/ui-filters/src/lib/filters.component';
import { negativeHeader$ } from '@valk-nx/core/lib/core';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { CarouselWithAvailabilityWidgetComponent as SbCarouselWithAvailabilityWidgetComponent } from '@valk-nx/storyblok/components/carousel-with-availability-widget/src/lib/carousel-with-availability-widget';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import type {
	FilterableHotelFilter,
	FilterableListItems,
	FilterableListScenario,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type {
	CarouselItem,
	HeadingGroupSection,
	ISbStoryBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { VdcHotels } from './lists/vdc-hotels/vdc-hotels';
import { VdcPackages } from './lists/vdc-packages/vdc-packages';
import type {
	FilterableListHotel,
	FilterableListType,
} from './filterable-list.interface';

const carouselComponents = {
	carousel: SbCarouselComponent,
	carouselWithAvailabilityWidget: SbCarouselWithAvailabilityWidgetComponent,
};

const titleComponents = {
	headingGroup: HeadingGroupComponent,
};

const richTextComponents = {
	richText: RichTextComponent,
};

const scenarioMap = {
	vdc: {
		hotels: 'vdc-hotels',
		packages: 'vdc-packages',
		events: 'vdc-events',
		rooms: 'vdc-rooms',
	},
	vdcvhi: {
		hotels: 'vdcvhi-hotels',
		packages: 'vdcvhi-packages',
		events: 'vdcvhi-events',
		rooms: 'vdcvhi-rooms',
	},
} as const satisfies Record<
	FilterableListHotel,
	Record<FilterableListType, FilterableListScenario>
>;

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-filterable-list',
	templateUrl: './filterable-list.html',
	imports: [
		CommonModule,
		DynamicModule,
		FiltersComponent,
		FilterHeadingComponent,
		TranslatePipe,
		VdcHotels,
		VdcPackages,
	],
})
export class FilterableList extends StoryblokRootDirective {
	protected readonly filterableListService = inject(FilterableListService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly metadataService = inject(MetadataService);

	hotel = input.required<FilterableListHotel>();
	type = input.required<FilterableListType>();
	carousel = input.required<ISbStoryBlok[]>();
	title = input.required<HeadingGroupSection[]>();
	content = input.required<ISbStoryBlok[]>();
	listItems = input<FilterableListItems>({});

	baseUrl = toSignal(
		this.activatedRoute.url.pipe(
			map(
				(segments) =>
					'/' +
					segments
						.map((s) => s.path)
						.filter(Boolean)
						.join('/'),
			),
		),
		{ initialValue: '/' },
	);

	sortOption = signal<string>('name-asc');

	activeFilterCount = computed(() =>
		Object.values(this.filterableListService.filters()).reduce(
			(sum, values) => sum + values.length,
			0,
		),
	);

	activeScenario = computed<FilterableListScenario | null>(() => {
		const hotel = this.hotel();
		const type = this.type();

		if (!hotel || !type) return null;

		return scenarioMap[hotel]?.[type] ?? null;
	});

	numberOfResults = computed(() => {
		if (this.activeScenario() === 'vdc-hotels') {
			return (
				this.filterableListService.vdcHotels.value()?.hotels.length ?? 0
			);
		}
		if (this.activeScenario() === 'vdc-packages') {
			return (
				this.filterableListService.vdcPackages.value()?.packageBatches
					.length ?? 0
			);
		}
		return 0;
	});

	filters = computed<FiltersInterface[]>(() => {
		const scenario = this.activeScenario();
		let rawFilters: FilterableHotelFilter[];

		if (scenario === 'vdc-hotels') {
			rawFilters = this.filterableListService.vdcHotels.value()?.filters;
		} else if (scenario === 'vdc-packages') {
			rawFilters =
				this.filterableListService.vdcPackages.value()?.filters;
		}

		if (!rawFilters) return [];

		return (
			rawFilters
				// TODO: Remove this filter once all filter types are supported
				.filter((filter) => filter.type === 'checkbox')
				.map(({ title, type, data }) => ({
					title,
					type,
					items: data.map(({ name, code, frequency }) => ({
						name,
						value: code,
						frequency,
					})),
				}))
		);
	});

	shouldShowCarousel = computed(() => this.carousel().length > 0);

	carouselOgImages = computed<PhotoInterface[]>(() => {
		const firstItem = (
			this.carousel()[0]?.['items'] as CarouselItem[] | undefined
		)?.[0];
		const bynder = firstItem?.bynder?.[0];

		if (!bynder) return [];

		return [
			{ src: bynder.files.transformBaseUrl.url, alt: firstItem.imageAlt },
		];
	});

	carouselInputs = computed<(InputsType & { component: string })[]>(() =>
		this.carousel().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		})),
	);

	contentInputs = computed<(InputsType & { component: string })[]>(() =>
		this.content().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
			isReadMore: true,
		})),
	);

	carouselComponents: typeof carouselComponents = carouselComponents;
	titleComponents: typeof titleComponents = titleComponents;
	richTextComponents: typeof richTextComponents = richTextComponents;

	constructor() {
		super();

		effect(() => {
			negativeHeader$.next(this.shouldShowCarousel());
		});

		effect(() => {
			this.metadataService.updateMetaDataBatch(
				{
					meta: {
						title: this.metaTitle(),
						description: this.metaDescription(),
					},
					images: this.carouselOgImages(),
				},
				this.robotsNoFollow,
				this.robotsNoIndex,
			);
		});

		effect(() => {
			this.filterableListService.scenario.set(
				this.activeScenario() ?? undefined,
			);
			this.filterableListService.filters.set({});
		});

		effect(() => {
			this.filterableListService.listItems.set(this.listItems());
		});
	}
}
